import React from "react";
import { Form, Input, Button, Card, message, Typography } from "antd";
import { Mail, Lock, Phone, Building, User } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import useUserStore from "../store/userStore";
import { useTranslation } from "../i18n/useTranslation";

const { Title, Text } = Typography;

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useUserStore();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // 组件加载时清除错误状态
  React.useEffect(() => {
    clearError();
  }, [clearError]);

  const onFinish = async (values) => {
    const result = await register(values);
    if (result.success) {
      message.success(t("registerSuccess"));
      navigate("/");
    } else {
      message.error(result.error);
    }
  };

  return (
    <div
      key="register"
      className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
    >
      <Card className="w-full max-w-md shadow-lg" bordered={false}>
        <div className="text-center mb-6">
          <Title level={3} className="mb-2">
            {t("registerTitle")}
          </Title>
          <Text type="secondary">{t("registerSubtitle")}</Text>
        </div>

        <Form form={form} name="register" layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="email"
            label={t("registerEmailLabel")}
            rules={[
              { required: true, message: t("registerEmailRequired") },
              { type: "email", message: t("registerEmailInvalid") },
            ]}
          >
            <Input
              prefix={<Mail className="text-gray-400" size={16} />}
              placeholder={t("registerEmailPlaceholder")}
              className="h-11"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={t("registerPasswordLabel")}
            rules={[
              { required: true, message: t("registerPasswordRequired") },
              { min: 8, message: t("registerPasswordMinLength") },
            ]}
          >
            <Input.Password
              prefix={<Lock className="text-gray-400" size={16} />}
              placeholder={t("registerPasswordPlaceholder")}
              className="h-11"
              visibilityToggle
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label={t("registerConfirmPasswordLabel")}
            dependencies={["password"]}
            rules={[
              { required: true, message: t("registerConfirmPasswordRequired") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("registerConfirmPasswordMismatch")));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<Lock className="text-gray-400" size={16} />}
              placeholder={t("registerConfirmPasswordPlaceholder")}
              className="h-11"
              visibilityToggle
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label={t("registerPhoneLabel")}
            rules={[
              { required: true, message: t("registerPhoneRequired") },
              { pattern: /^1[3-9]\d{9}$/, message: t("registerPhoneInvalid") },
            ]}
          >
            <Input
              prefix={<Phone className="text-gray-400" size={16} />}
              placeholder={t("registerPhonePlaceholder")}
              className="h-11"
            />
          </Form.Item>

          <Form.Item
            name="company_name"
            label={t("registerCompanyNameLabel")}
            rules={[
              { required: true, message: t("registerCompanyNameRequired") },
              { min: 2, message: t("registerCompanyNameMinLength") },
            ]}
          >
            <Input
              prefix={<Building className="text-gray-400" size={16} />}
              placeholder={t("registerCompanyNamePlaceholder")}
              className="h-11"
            />
          </Form.Item>

          <Form.Item className="mb-6">
            <Button
              type="primary"
              htmlType="submit"
              className="w-full h-11 text-base"
              loading={isLoading}
              size="large"
            >
              {t("registerButton")}
            </Button>
          </Form.Item>

          <div className="text-center">
            <Text type="secondary">
              {t("registerHasAccount")}
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                {t("registerLoginNow")}
              </Link>
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register;
